import type { Contract, ContractListPagination, ContractStatus } from "../types/contract";
import { mockContracts } from "../data/mockContracts";
import { clearSessionAndRedirectToLogin, isAuthenticated } from "./authService";
import { getGeneratedContractById, listMyGeneratedContracts } from "./generatedContractService";

const uploadedContracts: Contract[] = [];

function assertAuthenticated(): void {
  if (!isAuthenticated()) {
    clearSessionAndRedirectToLogin();
    throw new Error("Not authenticated");
  }
}

export type GetContractsParams = {
  page: number;
  pageSize: number;
  search: string;
  dateRangeDays?: 10 | 15 | 30;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
};

export type GetContractsResult = {
  items: Contract[];
  pagination: ContractListPagination;
  /** True if any row in the filtered list (all pages) is still in an AI/processing state that may update soon. */
  hasProcessingContracts: boolean;
};

const isProcessingPollStatus = (c: Contract): boolean =>
  c.status === "processing" || c.status === "ai_in_progress";

export const getContracts = async (params: GetContractsParams): Promise<GetContractsResult> => {
  assertAuthenticated();
  const all = await listMyGeneratedContracts();

  const q = params.search.trim().toLowerCase();
  let filtered = q
    ? all.filter(
        (c) => c.title.toLowerCase().includes(q) || c.type.toLowerCase().includes(q)
      )
    : all;

  if (params.dateRangeDays !== undefined) {
    const cutoff = Date.now() - params.dateRangeDays * 24 * 60 * 60 * 1000;
    filtered = filtered.filter((c) => {
      const t = Date.parse(c.date);
      return !Number.isNaN(t) && t >= cutoff;
    });
  }

  const pageSize = params.pageSize;
  const page = Math.max(1, params.page);
  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize) || 1);
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const items = filtered.slice(start, start + pageSize);
  const hasProcessingContracts = filtered.some(isProcessingPollStatus);

  return {
    items,
    pagination: {
      page: safePage,
      pageSize,
      totalItems,
      totalPages
    },
    hasProcessingContracts
  };
};

export const getContractById = async (id: string): Promise<Contract | null> => {
  assertAuthenticated();
  try {
    const fromApi = await getGeneratedContractById(id);
    if (fromApi) {
      return fromApi;
    }
  } catch {
    /* fall through to local fixtures */
  }
  const all = [...mockContracts, ...uploadedContracts];
  const contract = all.find((c) => c.id === id) ?? null;
  return contract ? { ...contract } : null;
};

export const addContract = (contract: Contract): void => {
  assertAuthenticated();
  uploadedContracts.push(contract);
};

export const updateContractStatus = (contractId: string, status: ContractStatus): void => {
  assertAuthenticated();
  const mock = mockContracts.find((c) => c.id === contractId);
  if (mock) mock.status = status;
  const uploaded = uploadedContracts.find((c) => c.id === contractId);
  if (uploaded) uploaded.status = status;
};
